import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Button from '../../components/Button';
import Caret from '../../components/Caret';
import PageMeta from '../../utils/PageMeta';
import OnboardingActions from '../OnboardingActions';
import useOnboardingData from '../useOnboardingData';
import TutorialModal from './TutorialModal';
import TutorialProgress from './TutorialProgress';

const stepsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    title: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    beacons: PropTypes.arrayOf(PropTypes.string),
    onActive: PropTypes.func,
  }),
);

const propTypes = {
  disableWaitForLoading: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onBackToList: PropTypes.func,
  onComplete: PropTypes.func,
  onSkip: PropTypes.func,
  openingTitle: PropTypes.string,
  steps: stepsPropType.isRequired,
};

const defaultProps = {
  disableWaitForLoading: false,
  onBackToList: null,
  onComplete: null,
  onSkip: null,
  openingTitle: null,
};

const Tutorial = ({
  steps,
  name,
  onBackToList,
  onComplete,
  onSkip,
  disableWaitForLoading,
  openingTitle,
}) => {
  const dispatch = useDispatch();

  const data = useOnboardingData(name);
  const { loading, onUpdateStep } = data;

  const handleComplete = () => {
    dispatch(OnboardingActions.setActiveTutorial(null));
    if (onComplete) {
      onComplete();
    }
    return data.onComplete();
  };

  const handleSkip = () => {
    dispatch(OnboardingActions.setActiveTutorial(null));
    if (onSkip) {
      onSkip();
    }
    return data.onSkip();
  };

  const stepIndex = data.onboarding ? data.onboarding.step : 0;
  const step = steps[stepIndex];

  useEffect(() => {
    if (loading || !step) {
      return undefined;
    }

    if (step.onActive) {
      step.onActive();
    }

    dispatch(OnboardingActions.setActiveBeacons(step.beacons));
    return () => dispatch(OnboardingActions.setActiveBeacons([]));
  }, [loading, step, dispatch]);

  if (!disableWaitForLoading && loading) {
    return null;
  }

  let modal = null;
  const initialStepActionProp = onBackToList
    ? {
        additionalControls: (
          <Button
            icon={<Caret direction="left" />}
            variant="link"
            onClick={onBackToList}
          >
            Back to List
          </Button>
        ),
        ...(onSkip
          ? {
              onSecondaryClick: handleSkip,
              secondaryButtonText: 'Skip',
            }
          : {}),
      }
    : {
        onSecondaryClick: handleSkip,
        secondaryButtonText: 'No, thanks',
      };

  if (stepIndex === 0) {
    modal = (
      <TutorialModal
        onPrimaryClick={() => onUpdateStep(1)}
        primaryButtonText="Let's go"
        {...initialStepActionProp}
        {...step}
        title={openingTitle || step.title}
      />
    );
  } else if (stepIndex === steps.length - 1) {
    modal = (
      <TutorialModal
        onPrimaryClick={handleComplete}
        onSecondaryClick={() => onUpdateStep(stepIndex - 1)}
        primaryButtonText="Done"
        secondaryButtonText="Back"
        additionalControls={
          <Button variant="link" onClick={() => onUpdateStep(0)}>
            Restart
          </Button>
        }
        {...step}
      />
    );
  } else {
    modal = (
      <TutorialModal
        additionalControls={
          <TutorialProgress total={steps.length - 2} at={stepIndex - 1} />
        }
        onPrimaryClick={() => onUpdateStep(stepIndex + 1)}
        onSecondaryClick={() => onUpdateStep(stepIndex - 1)}
        primaryButtonText="Continue"
        secondaryButtonText="Back"
        {...step}
      />
    );
  }

  const imageUrls = _.uniq(
    steps.map(({ imageUrl }) => imageUrl).filter((i) => i),
  );

  return (
    <>
      <PageMeta>
        {imageUrls?.map((imageUrl) => (
          <link key={imageUrl} rel="preload" as="image" href={imageUrl} />
        ))}
      </PageMeta>
      {modal}
    </>
  );
};

Tutorial.propTypes = propTypes;
Tutorial.defaultProps = defaultProps;

export default Tutorial;
